/**
 * Login (plan 032). Firebase Auth owns the password store; we call
 * `signInWithPassword` to prove the credential, then exchange the
 * resulting ID token at `/v1/auth/firebase/callback` for a Backstop
 * session. Wrap-key unlock happens locally with `tryLoadFamilyKey` —
 * same crypto as pre-032, just no CP-side login route in between.
 *
 * Two flows on this page:
 *
 *  1. Normal — email + password. Firebase auth → CP callback →
 *     tryLoadFamilyKey. Failure modes are surfaced with friendly
 *     inline messages (wrong password server-side, wrong password
 *     local, empty vault).
 *  2. Recover with mnemonic — user has a Firebase password + a 24-word
 *     phrase but the local vault is empty (fresh browser) or wrapped
 *     under a different password (browser used for another account,
 *     or Firebase password was reset). Enter phrase → derive family
 *     key → wrap under the just-entered password → route to dashboard.
 *
 * Forgot the password entirely? "Reset your account" links to the
 * ResetRequestRoute which calls Firebase's `sendPasswordResetEmail`.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import {
  firebaseConfigured,
  friendlyFirebaseError,
  signInWithGoogle,
  signInWithPassword,
} from '../data/firebase';
import { completeFirebaseCallback } from '../data/firebase-callback';
import { devAuthEnabled, devSignIn } from '../data/devAuth';
import { familyKeyFromMnemonic } from '../e2ee/mnemonic';
import { ApiError } from '../state/api';
import { setFlash } from '../state/flash';
import { captureIntentFromUrl } from '../state/checkout-intent';
import {
  storeFamilyKey,
  tryLoadFamilyKey,
  wipeLocalVault,
} from '../state/session';

export function LoginRoute() {
  const location = useLocation();
  const [mode, setMode] = useState<'normal' | 'recover'>('normal');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [showWipe, setShowWipe] = useState(false);

  useEffect(() => {
    captureIntentFromUrl();
  }, []);

  async function onWipeLocal(): Promise<void> {
    if (
      !window.confirm(
        "Wipe this browser's Backstop data? You will need your 24-word recovery phrase to restore access. Any unsent local edits will be lost.",
      )
    ) {
      return;
    }
    await wipeLocalVault(email);
    setErr(null);
    setShowWipe(false);
    setMode('recover');
  }

  async function onGoogle(): Promise<void> {
    setErr(null);
    setGoogleBusy(true);
    try {
      const idToken = await signInWithGoogle();
      const result = await completeFirebaseCallback({
        email,
        idToken,
        provider: 'google.com',
      });
      if (result.linked) {
        setFlash({
          kind: 'linked-google',
          message:
            'Linked your Google account to your existing Backstop login.',
        });
      }
      switch (result.next.kind) {
        case 'reveal-mnemonic':
          location.route('/onboarding/mnemonic');
          break;
        case 'dashboard':
          location.route('/dashboard');
          break;
        case 'restore-mnemonic':
          location.route('/login/restore-mnemonic');
          break;
      }
    } catch (e) {
      const msg = friendlyFirebaseError(e);
      if (msg !== '') setErr(msg);
    } finally {
      setGoogleBusy(false);
    }
  }

  async function onNormalSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setErr(null);
    setShowWipe(false);
    setBusy(true);
    try {
      // Firebase Auth check first. Wrong-email / wrong-password lands
      // here as an `auth/invalid-credential` throw with a clean message.
      let idToken: string;
      try {
        idToken = await signInWithPassword(email, password);
      } catch (fbErr) {
        setErr(friendlyFirebaseError(fbErr));
        return;
      }

      // Exchange for Backstop session.
      try {
        await completeFirebaseCallback({
          email,
          idToken,
          provider: 'password',
        });
      } catch (cbErr) {
        if (cbErr instanceof ApiError) {
          setErr(cbErr.detail);
        } else {
          setErr((cbErr as Error).message);
        }
        return;
      }

      // Unlock the local wrapped family key with the typed password.
      const unlock = await tryLoadFamilyKey(email, password, {
        remember: true,
      });
      if (unlock.kind === 'empty') {
        setErr(
          'This browser does not have a family-key wrap for this account. Click "Recover with mnemonic" below to restore access with your 24-word phrase.',
        );
        return;
      }
      if (unlock.kind === 'wrong-password') {
        setShowWipe(true);
        setErr(
          'Signed in, but this browser holds a wrap under a different password (a previous Backstop account here, or a Firebase password reset since you last unlocked). Wipe + recover, or type the older password you set on this browser.',
        );
        return;
      }
      location.route('/dashboard');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onRecoverSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      let idToken: string;
      try {
        idToken = await signInWithPassword(email, password);
      } catch (fbErr) {
        setErr(friendlyFirebaseError(fbErr));
        return;
      }

      // Derive the family key from the mnemonic and wrap it locally
      // under the password the user just typed. Any prior vault entry
      // for this email gets overwritten.
      let fk: Uint8Array;
      try {
        fk = familyKeyFromMnemonic(mnemonic);
      } catch (mnErr) {
        setErr(
          `Could not decode the recovery phrase: ${(mnErr as Error).message}`,
        );
        return;
      }
      await storeFamilyKey(email, password, fk);

      await completeFirebaseCallback({
        email,
        idToken,
        provider: 'password',
      });
      location.route('/dashboard');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onDevSignIn(): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      await devSignIn();
      location.route('/dashboard');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'recover') {
    return (
      <main class="flex min-h-screen items-center justify-center p-4">
        <Card class="w-full max-w-md space-y-4">
          <h1 class="text-2xl font-semibold">Recover with mnemonic</h1>
          <p class="text-sm text-surface-fg/70">
            Enter your Backstop password + your 24-word recovery phrase.
            We&apos;ll re-encrypt your family key on this browser under the same
            password.
          </p>
          <form onSubmit={onRecoverSubmit} class="space-y-3" noValidate>
            <label class="block space-y-1 text-sm">
              <span>Email</span>
              <Input
                type="email"
                value={email}
                onInput={(e: Event) => {
                  setEmail((e.target as HTMLInputElement).value);
                }}
                required
                autocomplete="email"
              />
            </label>
            <label class="block space-y-1 text-sm">
              <span>Password</span>
              <Input
                type="password"
                value={password}
                onInput={(e: Event) => {
                  setPassword((e.target as HTMLInputElement).value);
                }}
                required
                autocomplete="current-password"
              />
            </label>
            <label class="block space-y-1 text-sm">
              <span>Recovery phrase (24 words)</span>
              <textarea
                class="min-h-24 w-full rounded border border-surface-muted/40 bg-surface p-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                value={mnemonic}
                onInput={(e: Event) => {
                  setMnemonic((e.target as HTMLTextAreaElement).value);
                }}
                spellcheck={false}
                autocapitalize="none"
                required
              />
            </label>
            {err !== null && (
              <p class="text-sm text-danger-500" role="alert">
                {err}
              </p>
            )}
            <div class="flex gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? 'Recovering…' : 'Recover'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setMode('normal');
                  setErr(null);
                }}
              >
                Back to login
              </Button>
            </div>
            <p class="text-sm text-surface-muted pt-2 border-t border-line">
              Lost your phrase too?{' '}
              <a href="/reset-request" class="text-signal underline">
                Reset your password
              </a>{' '}
              &mdash; your saved data stays wrapped only under the phrase, so
              you&apos;ll lose it.
            </p>
          </form>
        </Card>
      </main>
    );
  }

  return (
    <main class="flex min-h-screen items-center justify-center p-4">
      <Card class="w-full max-w-md space-y-4">
        <h1 class="text-2xl font-semibold">Log in</h1>
        {firebaseConfigured() && (
          <>
            <Button
              type="button"
              onClick={() => void onGoogle()}
              disabled={googleBusy || busy}
              variant="secondary"
              class="w-full"
            >
              {googleBusy ? 'Opening Google…' : 'Continue with Google'}
            </Button>
            <div
              class="relative my-1 text-center text-xs uppercase tracking-wide text-surface-fg/50"
              aria-hidden="true"
            >
              <span class="relative z-10 bg-surface-card px-2">or</span>
              <span class="absolute left-0 right-0 top-1/2 -z-0 h-px bg-surface-fg/20" />
            </div>
          </>
        )}
        <form onSubmit={onNormalSubmit} class="space-y-3" noValidate>
          <label class="block space-y-1 text-sm">
            <span>Email</span>
            <Input
              type="email"
              value={email}
              onInput={(e: Event) => {
                setEmail((e.target as HTMLInputElement).value);
              }}
              required
              autocomplete="email"
            />
          </label>
          <label class="block space-y-1 text-sm">
            <span>Password</span>
            <Input
              type="password"
              value={password}
              onInput={(e: Event) => {
                setPassword((e.target as HTMLInputElement).value);
              }}
              required
              autocomplete="current-password"
            />
          </label>
          {err !== null && (
            <p class="text-sm text-danger-500" role="alert">
              {err}
            </p>
          )}
          {showWipe && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void onWipeLocal()}
            >
              Wipe this browser and recover
            </Button>
          )}
          <Button type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Log in'}
          </Button>
        </form>
        <Button
          variant="ghost"
          onClick={() => {
            setMode('recover');
            setErr(null);
          }}
        >
          Recover with mnemonic
        </Button>
        {devAuthEnabled() ? (
          <Button
            type="button"
            variant="secondary"
            class="w-full"
            disabled={busy}
            onClick={() => void onDevSignIn()}
          >
            Dev sign-in — skip auth, local data only
          </Button>
        ) : null}
        <p class="text-sm text-surface-muted pt-2 border-t border-line">
          Forgot password?{' '}
          <a href="/reset-request" class="text-signal underline">
            Reset it via email
          </a>
          . Forgot password AND phrase? You&apos;ll need to start a fresh
          account and lose your saved data.
        </p>
      </Card>
    </main>
  );
}
